/**
 * SpecialEventScanPage — Front-door QR scanner for special events
 *
 * Route: /admin/special-events/scan
 * Roles: super_admin | manager | reception | gate_scan
 *
 * Flow:
 *   1. Camera-based QR scan via html5-qrcode (rear camera preferred)
 *   2. QR encodes the compact `qr_token` (also accepts full ticket URL)
 *   3. POST /api/admin/special-events/{slug}/scan { token, checkin: true }
 *   4. Result card shows customer, items, and **BALANCE DUE AT GATE**
 *      prominently. Admin can mark balance as collected or undo check-in.
 *   5. Cooldown to prevent double-scans.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { toast } from 'sonner';
import {
  Scan, Camera, CameraOff, Check, AlertCircle, Loader2, Keyboard,
  User, Wallet, Sparkles, CheckCircle2, XCircle, DoorOpen, IndianRupee,
} from 'lucide-react';

import AdminShell from '../../components/admin/AdminShell';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { adminApi } from '../../lib/api';
import { cn } from '../../lib/utils';

const SLUG = 'nazakat-shararat';
const SCANNER_ID = 'sxe-scan-frame';
const SCAN_COOLDOWN = 1800; // ms — block re-decode of same QR while in frame

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function classifyCameraError(err) {
  const name = err?.name || '';
  const msg = (err?.message || '').toLowerCase();
  if (name === 'NotAllowedError' || msg.includes('permission')) {
    return { title: 'Camera permission denied', hint: 'Allow camera access in your browser settings and refresh.' };
  }
  if (name === 'NotFoundError' || msg.includes('not found')) {
    return { title: 'No camera found', hint: 'Connect a camera or use manual entry.' };
  }
  return { title: 'Could not start camera', hint: err?.message || 'Unknown error' };
}

function StatusPill({ result }) {
  if (!result) return null;
  if (result.action === 'checked_in_now') {
    return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs" data-testid="scan-status-ok">Check-in Successful</Badge>;
  }
  if (result.action === 'already_checked_in') {
    return <Badge className="bg-amber-500/15 text-amber-200 border border-amber-500/30 text-xs" data-testid="scan-status-warn">Already Checked-in</Badge>;
  }
  if (result.error === 'cancelled') {
    return <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-xs" data-testid="scan-status-err">Cancelled Booking</Badge>;
  }
  if (result.error === 'not_paid') {
    return <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-xs" data-testid="scan-status-err">Payment Pending</Badge>;
  }
  if (result.ok) {
    return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs" data-testid="scan-status-ok">Valid Ticket</Badge>;
  }
  return <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-xs" data-testid="scan-status-err">Invalid</Badge>;
}

// ═══════════════════════════════════════════════════════════════════════════
// ResultCard
// ═══════════════════════════════════════════════════════════════════════════
function ResultCard({ result, error, busy, method, setMethod, onCollectBalance, onUndoCheckin, onScanNext }) {
  if (error) {
    return (
      <div className="rounded-xl border border-red-500/40 bg-red-500/8 p-6" data-testid="scan-error-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={18} className="text-red-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">{error.title}</p>
            <p className="text-sm text-zinc-300 mt-0.5">{error.detail}</p>
          </div>
        </div>
        <Button className="w-full mt-4 bg-[#D4A843] text-black hover:bg-[#C49A35]" onClick={onScanNext} data-testid="scan-next-btn">
          <Scan size={14} className="mr-2" /> Scan Next
        </Button>
      </div>
    );
  }

  if (!result) return null;
  const b = result.booking || {};
  const balanceDue = Number(result.balance_due || 0);
  const isValid = result.ok && !result.error;
  const isCheckedIn = !!b.checked_in;
  const wasJustCheckedIn = result.action === 'checked_in_now';
  const alreadyChecked = result.action === 'already_checked_in';

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5 space-y-4" data-testid="scan-result-card">
      {/* Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {wasJustCheckedIn ? (
            <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <CheckCircle2 size={18} className="text-emerald-300" />
            </div>
          ) : alreadyChecked ? (
            <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <DoorOpen size={18} className="text-amber-300" />
            </div>
          ) : result.error ? (
            <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
              <XCircle size={18} className="text-red-300" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <Check size={18} className="text-emerald-300" />
            </div>
          )}
          <div>
            <p className="text-white font-semibold text-lg leading-tight" data-testid="scan-guest-name">{b.customer_name || '—'}</p>
            <p className="text-xs text-zinc-500">{b.customer_phone_e164 || '—'}</p>
          </div>
        </div>
        <StatusPill result={result} />
      </div>

      {/* Error banner (non-valid tickets) */}
      {result.error && result.message && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/8 px-3 py-2 text-sm text-red-200">
          {result.message}
        </div>
      )}

      {/* Balance DUE — the most important thing at the gate */}
      {isValid && balanceDue > 0 && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 text-center" data-testid="scan-balance-panel">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5 flex items-center justify-center gap-1">
            <Wallet size={12} /> Balance Due at Gate
          </p>
          <p className="text-4xl font-semibold text-white tabular-nums" data-testid="scan-balance-amount">
            {fmtINR(balanceDue)}
          </p>
          <p className="text-xs text-amber-100/70 mt-1">Collect before entry.</p>
        </div>
      )}
      {isValid && balanceDue <= 0 && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-3 py-2 text-sm text-emerald-200 text-center" data-testid="scan-balance-cleared">
          <CheckCircle2 size={14} className="inline mr-1" /> Balance cleared — full amount paid
        </div>
      )}

      {/* Order + Items */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded border border-zinc-800 bg-zinc-900/50 p-2">
          <p className="text-[9px] uppercase tracking-widest text-zinc-500">Order</p>
          <p className="font-mono text-[#D4A843] mt-0.5">{b.payment_order_id || '—'}</p>
        </div>
        <div className="rounded border border-zinc-800 bg-zinc-900/50 p-2">
          <p className="text-[9px] uppercase tracking-widest text-zinc-500">Total Paid Online</p>
          <p className="text-emerald-300 mt-0.5 tabular-nums">{fmtINR(b.pay_now)}</p>
        </div>
      </div>

      {Array.isArray(b.items) && b.items.length > 0 && (
        <div className="border-t border-zinc-800 pt-3">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Passes</p>
          <ul className="space-y-1 text-sm">
            {b.items.map((it) => (
              <li key={it.sku_id} className="flex justify-between text-zinc-200">
                <span>{it.label || it.sku_id} × {it.qty}</span>
                <span className="tabular-nums text-zinc-500">{fmtINR(it.line_total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      {isValid && balanceDue > 0 && !b.balance_paid_at && (
        <div className="border-t border-zinc-800 pt-3 space-y-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Collection method</label>
          <div className="flex gap-2">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="scan-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="pos">POS</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button
              className="bg-amber-500 hover:bg-amber-500/90 text-black font-medium flex-1"
              disabled={busy}
              onClick={onCollectBalance}
              data-testid="scan-collect-balance-btn"
            >
              <IndianRupee size={14} className="mr-1.5" /> Collect {fmtINR(balanceDue)}
            </Button>
          </div>
        </div>
      )}

      {isCheckedIn && (
        <div className="text-[11px] text-zinc-500 border-t border-zinc-800 pt-2">
          Checked-in at {b.checked_in_at ? new Date(b.checked_in_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'} by {b.checked_in_by || 'admin'}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        {isCheckedIn && (
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-900"
            disabled={busy}
            onClick={onUndoCheckin}
            data-testid="scan-undo-checkin-btn"
          >
            Undo check-in
          </Button>
        )}
        <Button
          className="flex-1 bg-[#D4A843] text-black hover:bg-[#C49A35] font-medium"
          onClick={onScanNext}
          data-testid="scan-next-btn"
        >
          <Scan size={14} className="mr-2" /> Scan Next Guest
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════
export default function SpecialEventScanPage() {
  const scannerRef = useRef(null);
  const cooldownRef = useRef(0);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState('cash');

  // ── Submit token to backend ─────────────────────────────────────────
  const submitToken = useCallback(async (token) => {
    if (!token || submitting) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminApi.sxeAdminScan(SLUG, {
        token: token.trim(),
        checkin: true,
        force: false,
      });
      const data = res?.data || {};
      setResult(data);
      if (data.error === 'ticket_not_found') {
        setError({ title: 'Booking not found', detail: 'No booking matches this QR for this event.' });
        toast.error('Booking not found');
      } else if (data.error === 'cancelled') {
        toast.error('Booking is cancelled');
      } else if (data.error === 'not_paid') {
        toast.error('Payment not yet confirmed');
      } else if (data.action === 'already_checked_in') {
        const t = data.booking?.checked_in_at ? new Date(data.booking.checked_in_at).toLocaleTimeString('en-IN') : '';
        toast.warning(`Already checked in${t ? ` at ${t}` : ''}`);
      } else if (data.action === 'checked_in_now') {
        toast.success(`✓ ${data.booking?.customer_name || 'Guest'} checked in`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || err?.message || 'Scan failed');
      const status = err?.response?.status;
      setError({
        title:  status === 404 ? 'Booking not found' : status === 403 ? 'Not permitted' : 'Scan failed',
        detail: msg,
      });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [submitting]);

  // ── Cooldown decode ─────────────────────────────────────────────────
  const handleDecoded = useCallback((text) => {
    const now = Date.now();
    if (now - cooldownRef.current < SCAN_COOLDOWN) return;
    cooldownRef.current = now;
    submitToken(text);
  }, [submitToken]);

  // ── Start / stop camera ─────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError({ title: 'Camera unsupported', hint: 'Use Chrome/Safari over HTTPS, or use manual entry.' });
      setManualMode(true);
      return;
    }
    try {
      const qr = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = qr;
      await qr.start(
        { facingMode: { ideal: 'environment' } },
        {
          fps: 12,
          qrbox: (vw, vh) => {
            const minDim = Math.min(vw, vh);
            const size = Math.max(220, Math.floor(minDim * 0.75));
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          videoConstraints: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        (decodedText) => handleDecoded(decodedText),
        () => { /* per-frame errors are noisy */ },
      );
      setScanning(true);
    } catch (err) {
      const info = classifyCameraError(err);
      setCameraError(info);
      setScanning(false);
    }
  }, [handleDecoded]);

  const stopCamera = useCallback(async () => {
    const qr = scannerRef.current;
    if (!qr) return;
    try { await qr.stop(); await qr.clear(); } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  }, []);

  useEffect(() => {
    startCamera();
    return () => { stopCamera(); };
  }, [startCamera, stopCamera]);

  const handleScanNext = async () => {
    setResult(null);
    setError(null);
    setManualToken('');
    cooldownRef.current = 0;
    if (!scanning) await startCamera();
  };

  const handleManualSubmit = async (e) => {
    e?.preventDefault?.();
    if (!manualToken.trim()) return;
    await stopCamera();
    submitToken(manualToken);
  };

  const handleCollectBalance = async () => {
    if (!result?.booking?.id) return;
    setSubmitting(true);
    try {
      const res = await adminApi.sxeAdminPatchBooking(SLUG, result.booking.id, {
        action: 'mark_balance_paid',
        payment_method: method,
      });
      const nb = res?.data?.booking;
      if (nb) {
        setResult((r) => ({
          ...r,
          booking: nb,
          balance_due: Math.max(0, (nb.pay_at_gate || 0) - (nb.balance_collected || 0)),
        }));
      }
      toast.success(`Balance ${fmtINR(nb?.balance_collected || 0)} collected via ${method}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark balance paid');
    } finally { setSubmitting(false); }
  };

  const handleUndoCheckin = async () => {
    if (!result?.booking?.id) return;
    setSubmitting(true);
    try {
      const res = await adminApi.sxeAdminPatchBooking(SLUG, result.booking.id, {
        action: 'undo_checkin',
      });
      const nb = res?.data?.booking;
      if (nb) setResult((r) => ({ ...r, booking: nb, action: 'ok' }));
      toast.success('Check-in reverted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not undo check-in');
    } finally { setSubmitting(false); }
  };

  const showResultOrError = result || error;

  return (
    <AdminShell title="Scan Special Event Pass">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="sxe-scan-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#D4A843]" />
              <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] leading-tight">Scan Pass · Nazakat &amp; Shararat</h1>
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              Point your camera at the guest&apos;s QR. Balance due (if any) will appear on scan.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-zinc-800 text-zinc-200 hover:bg-zinc-900"
              onClick={() => setManualMode((v) => !v)}
              data-testid="scan-toggle-manual"
            >
              <Keyboard size={14} className="mr-2" />
              {manualMode ? 'Use camera' : 'Manual entry'}
            </Button>
          </div>
        </div>

        {/* Scanner / Manual */}
        {!showResultOrError && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
            {!manualMode ? (
              <>
                <div className="relative rounded-lg overflow-hidden bg-black aspect-square max-w-md mx-auto">
                  <div id={SCANNER_ID} className="w-full h-full" data-testid="sxe-scan-video" />
                  {!scanning && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-zinc-400 text-sm">
                      {cameraError ? (
                        <div className="text-center px-4">
                          <CameraOff size={22} className="mx-auto mb-2 text-red-400" />
                          <p className="text-white font-medium">{cameraError.title}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{cameraError.hint}</p>
                        </div>
                      ) : (
                        <>
                          <Loader2 size={18} className="animate-spin mr-2 text-[#D4A843]" /> Starting camera…
                        </>
                      )}
                    </div>
                  )}
                  {scanning && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-[12%] rounded-lg border-2 border-[#D4A843]/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Camera size={12} /> {scanning ? 'Scanning…' : 'Idle'}</span>
                  {submitting && <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Verifying…</span>}
                </div>
              </>
            ) : (
              <form onSubmit={handleManualSubmit} className="space-y-2 max-w-md mx-auto">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Paste QR token</label>
                <Input
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="e.g. ZCw4M0-U_njOBVqPJZFlymEzwYdO-NIC"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-sm"
                  autoFocus
                  data-testid="sxe-scan-manual-input"
                />
                <Button
                  type="submit"
                  className="w-full bg-[#D4A843] text-black hover:bg-[#C49A35] font-medium"
                  disabled={submitting || !manualToken.trim()}
                  data-testid="sxe-scan-manual-submit"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" />}
                  Verify pass
                </Button>
              </form>
            )}
          </div>
        )}

        {/* Result */}
        {showResultOrError && (
          <ResultCard
            result={result}
            error={error}
            busy={submitting}
            method={method}
            setMethod={setMethod}
            onCollectBalance={handleCollectBalance}
            onUndoCheckin={handleUndoCheckin}
            onScanNext={handleScanNext}
          />
        )}
      </div>
    </AdminShell>
  );
}
